import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Link } from "react-router-dom";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { useAuth } from "@/contexts/AuthContext";
import BackButton from "@/components/BackButton";
import { toast } from "sonner";
import { ShieldCheck, Receipt, Boxes, Wallet, Factory, ScrollText, Plus, CheckCircle2 } from "lucide-react";

function Card({ label, value, icon: Icon, sub, kind }) {
  const content = (
    <div className="surface-card p-5 hover:-translate-y-1 hover:shadow-sm transition-all cursor-pointer h-full" data-testid={`audit-card-${label.toLowerCase().replace(/\s/g, "-")}`}>
      <div className="flex items-center justify-between">
        <span className="text-xs uppercase tracking-wider font-bold text-muted-foreground">{label}</span>
        {Icon ? <Icon className="w-4 h-4" /> : null}
      </div>
      <div className="font-display text-2xl sm:text-3xl font-black mt-3">{value}</div>
      {sub ? <div className="text-[11px] text-muted-foreground mt-1">{sub}</div> : null}
    </div>
  );
  return kind ? <Link to={`/org/audit-ca/${kind}`}>{content}</Link> : content;
}

export default function OrgAuditCA() {
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [notes, setNotes] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ title: "", category: "GST", severity: "OBSERVATION", body: "", period: "" });

  const isCA = user?.role === "CA";
  const isOrgAdmin = user?.role === "ORG_ADMIN";

  const loadAll = () => {
    api.get("/org/audit-overview").then(({ data }) => setData(data)).catch(() => setData({ counts: {}, financial: {}, recent_audit: [] }));
    api.get("/org/audit-notes").then(({ data }) => setNotes(data)).catch(() => setNotes([]));
  };
  useEffect(() => { loadAll(); }, []);

  const submit = async () => {
    if (!form.title || !form.body) { toast.error("Title and note body are required"); return; }
    try {
      await api.post("/org/audit-notes", form);
      toast.success("Audit note submitted");
      setOpen(false);
      setForm({ title: "", category: "GST", severity: "OBSERVATION", body: "", period: "" });
      loadAll();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  const resolve = async (n) => {
    try { await api.patch(`/org/audit-notes/${n.id}`, { status: "RESOLVED" }); toast.success("Marked resolved"); loadAll(); }
    catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  if (!data) {
    return (
      <div className="p-4 sm:p-6 lg:p-8" data-testid="audit-ca-page">
        <div className="surface-card p-8 text-center text-muted-foreground">Loading audit snapshot…</div>
      </div>
    );
  }

  const c = data.counts || {};
  const f = data.financial || {};

  return (
    <div className="p-4 sm:p-6 lg:p-8 space-y-4 sm:space-y-6" data-testid="audit-ca-page">
      <BackButton />
      <div className="flex items-start justify-between flex-wrap gap-3">
        <div>
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-emerald-50 border border-emerald-200 text-[10px] font-bold uppercase tracking-[0.25em] text-emerald-700">
            <ShieldCheck className="w-3 h-3" /> CA Audit {isCA ? "· CA workspace" : "· read-only"}
          </div>
          <h1 className="font-display text-2xl sm:text-3xl font-black mt-3">Auditor workspace</h1>
          <p className="text-muted-foreground mt-1">
            Org-wide snapshot for chartered accountants — every department, every transaction.
            Generated at {new Date(data.generated_at).toLocaleString()}.
          </p>
        </div>
        {(isCA || isOrgAdmin) ? (
          <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild><Button data-testid="new-audit-note-btn"><Plus className="w-4 h-4 mr-1" /> New audit note</Button></DialogTrigger>
            <DialogContent className="max-w-xl max-h-[90vh] overflow-y-auto">
              <DialogHeader><DialogTitle>New CA audit note</DialogTitle></DialogHeader>
              <div className="space-y-3">
                <div><Label>Title*</Label><Input value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} data-testid="cn-title" /></div>
                <div className="grid grid-cols-2 gap-3">
                  <div><Label>Category</Label>
                    <Select value={form.category} onValueChange={(v) => setForm({ ...form, category: v })}>
                      <SelectTrigger data-testid="cn-category"><SelectValue /></SelectTrigger>
                      <SelectContent>{["GST", "TDS", "Compliance", "Suggestion", "Reconciliation", "Other"].map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
                    </Select>
                  </div>
                  <div><Label>Severity</Label>
                    <Select value={form.severity} onValueChange={(v) => setForm({ ...form, severity: v })}>
                      <SelectTrigger data-testid="cn-severity"><SelectValue /></SelectTrigger>
                      <SelectContent>{["INFO", "OBSERVATION", "WARNING", "CRITICAL"].map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
                    </Select>
                  </div>
                </div>
                <div><Label>Period</Label><Input placeholder="e.g. FY 2025-26, Q1" value={form.period} onChange={(e) => setForm({ ...form, period: e.target.value })} /></div>
                <div><Label>Details*</Label><Textarea rows={5} value={form.body} onChange={(e) => setForm({ ...form, body: e.target.value })} data-testid="cn-body" /></div>
                <Button onClick={submit} className="w-full" data-testid="cn-save">Submit note</Button>
              </div>
            </DialogContent>
          </Dialog>
        ) : null}
      </div>

      <div>
        <h2 className="font-display text-lg font-black mb-3">Financial position</h2>
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-3">
          <Card label="A/R Total" icon={Receipt} value={`₹${Number(f.ar_total || 0).toLocaleString()}`} sub="Sum across all invoices" />
          <Card label="A/R Outstanding" icon={Receipt} value={`₹${Number(f.ar_outstanding || 0).toLocaleString()}`} sub="Pending collections" />
          <Card label="Open PO value" icon={Wallet} value={`₹${Number(f.ap_open_po || 0).toLocaleString()}`} sub="Outstanding purchase orders" />
          <Card label="Stock value" icon={Boxes} value={`₹${Number(f.stock_value || 0).toLocaleString()}`} sub="Qty × avg cost" />
        </div>
      </div>

      <div>
        <h2 className="font-display text-lg font-black mb-3">Department inventory</h2>
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-3">
          <Card label="Users" value={c.users || 0} icon={ShieldCheck} kind="users" />
          <Card label="Items" value={c.items || 0} icon={Boxes} kind="items" />
          <Card label="Warehouses" value={c.warehouses || 0} icon={Factory} kind="warehouses" />
          <Card label="Purchase orders" value={c.purchase_orders || 0} icon={Receipt} kind="purchase_orders" />
          <Card label="Requisitions" value={c.requisitions || 0} icon={ScrollText} kind="requisitions" />
          <Card label="GRNs" value={c.grns || 0} icon={Boxes} kind="grns" />
          <Card label="Sales invoices" value={c.sales_invoices || 0} icon={Receipt} kind="sales_invoices" />
          <Card label="Payments" value={c.payments || 0} icon={Wallet} kind="payments" />
          <Card label="Credit notes" value={c.credit_notes || 0} icon={Receipt} kind="credit_notes" />
          <Card label="Production orders" value={c.production_orders || 0} icon={Factory} kind="production_orders" />
          <Card label="QC inspections" value={c.qc_inspections || 0} icon={ShieldCheck} kind="qc_inspections" />
          <Card label="Audit events" value={c.audit_events || 0} icon={ScrollText} kind="audit_events" />
        </div>
      </div>

      <div>
        <h2 className="font-display text-lg font-black mb-3">CA audit notes</h2>
        <div className="surface-card p-4 overflow-x-auto" data-testid="ca-notes-section">
          <Table>
            <TableHeader><TableRow><TableHead>Number</TableHead><TableHead>Title</TableHead><TableHead>Category</TableHead><TableHead>Severity</TableHead><TableHead>Period</TableHead><TableHead>By</TableHead><TableHead>Status</TableHead><TableHead></TableHead></TableRow></TableHeader>
            <TableBody>
              {notes.map((n) => (
                <TableRow key={n.id} data-testid={`ca-note-row-${n.id}`}>
                  <TableCell className="font-mono text-xs">{n.number}</TableCell>
                  <TableCell className="font-medium">{n.title}<div className="text-xs text-muted-foreground">{n.body?.slice(0, 100)}{(n.body?.length || 0) > 100 ? "…" : ""}</div></TableCell>
                  <TableCell><Badge variant="outline" className="font-mono text-[10px]">{n.category}</Badge></TableCell>
                  <TableCell><Badge variant="outline" className={`font-mono text-[10px] ${n.severity === "CRITICAL" ? "bg-red-100 text-red-900 border-red-200" : n.severity === "WARNING" ? "bg-amber-100 text-amber-900" : "bg-blue-100 text-blue-900"}`}>{n.severity}</Badge></TableCell>
                  <TableCell className="text-xs">{n.period || "—"}</TableCell>
                  <TableCell className="text-xs">{n.created_by}<div className="text-[10px] text-muted-foreground">{n.created_by_role}</div></TableCell>
                  <TableCell><Badge variant="outline" className={`font-mono text-[10px] ${n.status === "RESOLVED" ? "bg-emerald-100 text-emerald-900 border-emerald-200" : "bg-amber-50 text-amber-900"}`}>{n.status}</Badge></TableCell>
                  <TableCell>
                    {isOrgAdmin && n.status !== "RESOLVED" ? (
                      <Button size="sm" variant="outline" onClick={() => resolve(n)} data-testid={`ca-note-resolve-${n.id}`}><CheckCircle2 className="w-3.5 h-3.5 mr-1" /> Resolve</Button>
                    ) : null}
                  </TableCell>
                </TableRow>
              ))}
              {notes.length === 0 && <TableRow><TableCell colSpan={8} className="text-center text-muted-foreground py-8">No CA notes yet. {isCA ? "Click 'New audit note' to submit an observation for the Org Admin." : ""}</TableCell></TableRow>}
            </TableBody>
          </Table>
        </div>
      </div>

      <div>
        <h2 className="font-display text-lg font-black mb-3">Recent audit trail</h2>
        <div className="surface-card p-4 overflow-x-auto">
          <Table>
            <TableHeader><TableRow><TableHead>When</TableHead><TableHead>Action</TableHead><TableHead>Entity</TableHead><TableHead>Actor</TableHead></TableRow></TableHeader>
            <TableBody>
              {(data.recent_audit || []).map((ev) => (
                <TableRow key={ev.id} data-testid={`audit-row-${ev.id}`}>
                  <TableCell className="text-xs font-mono">{ev.created_at ? new Date(ev.created_at).toLocaleString() : "—"}</TableCell>
                  <TableCell><Badge variant="outline" className="font-mono text-[10px]">{ev.action}</Badge></TableCell>
                  <TableCell className="text-xs">{ev.entity_type} · <code className="font-mono">{(ev.entity_id || "").slice(0, 8)}</code></TableCell>
                  <TableCell className="text-xs">{ev.actor || "—"}</TableCell>
                </TableRow>
              ))}
              {(!data.recent_audit || data.recent_audit.length === 0) && (
                <TableRow><TableCell colSpan={4} className="text-center text-muted-foreground py-8">No audit events yet.</TableCell></TableRow>
              )}
            </TableBody>
          </Table>
        </div>
      </div>
    </div>
  );
}
